import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Droplets, Thermometer, Layers, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, itemVariant } from "../components/Reveal";
import { ProcessFlow } from "../components/ProcessFlow";
import { ShoreScale } from "../components/ShoreScale";
import { FaqBlock, faqSchema } from "../components/FaqBlock";
import { ComparisonTable } from "../components/ComparisonTable";
import { TPU_PRODUCTS, TPU_CATEGORIES, IMAGES, TPU_APP_NAV, TPU_COMPARE } from "../data/content";

const ADV = [
  { de: ["Hohe Abriebfestigkeit", "Verschleißarm auch bei Dauerbelastung"], en: ["High abrasion resistance", "Low wear even under continuous load"] },
  { de: ["Elastizität & Flexibilität", "Gummiähnliches Verhalten, thermoplastisch verarbeitbar"], en: ["Elasticity & flexibility", "Rubber-like behaviour, thermoplastically processable"] },
  { de: ["Chemikalienbeständigkeit", "Beständig gegen Öle, Fette und viele Chemikalien"], en: ["Chemical resistance", "Resistant to oils, greases and many chemicals"] },
  { de: ["Breiter Härtebereich", "60 bis 95 Shore A für viele Anwendungen"], en: ["Wide hardness range", "60 to 95 Shore A for many applications"] },
];

const FAQ = [
  { q: { de: "Welches TPU eignet sich für Extrusion?", en: "Which TPU is suitable for extrusion?" }, a: { de: "Für Extrusion (Schläuche, Profile, Folien, Kabel) werden Typen mit höherer Schmelzefestigkeit und passendem MFI eingesetzt, z. B. NORDFLEX® TPU T 85-A oder T 90-A.", en: "For extrusion (tubing, profiles, films, cables) grades with higher melt strength and suitable MFI are used, e.g. NORDFLEX® TPU T 85-A or T 90-A." } },
  { q: { de: "Wie wird TPU vor der Verarbeitung getrocknet?", en: "How is TPU dried before processing?" }, a: { de: "TPU ist hygroskopisch und sollte vor Spritzguss/Extrusion typischerweise bei 80–110 °C für 2–4 Stunden auf eine Restfeuchte < 0,02 % getrocknet werden. Genaue Werte je Typ im Datenblatt.", en: "TPU is hygroscopic and should typically be dried at 80–110 °C for 2–4 hours to a residual moisture < 0.02 % before injection/extrusion. Exact values per grade in the data sheet." } },
  { q: { de: "Was ist der Unterschied zwischen Neuware und Regranulat?", en: "Difference between virgin material and regranulate?" }, a: { de: "Neuware ist unverarbeitetes Originalmaterial. Regranulat aus Neuware ist homogenisiertes, laborgeprüftes Material – die wirtschaftliche Alternative mit dokumentierter Qualität.", en: "Virgin is unprocessed original material. Regranulate from virgin is homogenized, lab-tested material – the economical alternative with documented quality." } },
  { q: { de: "Bietet PlastoNord flammhemmendes TPU an?", en: "Does PlastoNord offer flame-retardant TPU?" }, a: { de: "Ja, FR-Typen wie NORDFLEX® TPU S 85-A FR für Elektro- und Sicherheitsanwendungen.", en: "Yes, FR grades such as NORDFLEX® TPU S 85-A FR for electrical and safety applications." } },
];

export default function TPU() {
  const { t, lang, pick } = useLang();
  const L = (o) => (lang === "de" ? o.de : o.en);
  return (
    <>
      <Seo
        title="TPU Granulat – Thermoplastisches Polyurethan | NORDFLEX® | PlastoNord"
        description="NORDFLEX® TPU Granulat von PlastoNord: thermoplastisches Polyurethan für Spritzguss & Extrusion. Shore A 60–95, Neuware, FR-Typen, Regranulat. TPU Lieferant & Hersteller in Deutschland."
        path="/tpu"
        image={IMAGES.macro}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "TPU", path: "/tpu" }]}
        schema={faqSchema(FAQ, pick)}
      />
      <PageHero
        overline="NORDFLEX® · Thermoplastisches Polyurethan"
        title={lang === "de" ? "TPU Granulat für Spritzguss, Extrusion und Industrie" : "TPU granules for injection molding, extrusion and industry"}
        subtitle={lang === "de" ? "Thermoplastisches Polyurethan (TPU) verbindet die Elastizität von Gummi mit der Verarbeitbarkeit von Thermoplasten – als Neuware, FR-Typ, Masterbatch oder Regranulat." : "Thermoplastic polyurethane (TPU) combines rubber elasticity with thermoplastic processability – as virgin, FR grade, masterbatch or regranulate."}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/kontakt" className="btn-primary" data-testid="tpu-request">{t("cta.request")} <ArrowRight size={16} /></Link>
          <Link to="/downloads" className="btn-ghost">{t("nav.downloads")}</Link>
        </div>
      </PageHero>

      {/* WHAT IS TPU */}
      <section className="py-24">
        <div className="pn-container grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="overline">{lang === "de" ? "Was ist TPU?" : "What is TPU?"}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "Die Brücke zwischen Gummi und Kunststoff" : "The bridge between rubber and plastic"}</h2>
            <p className="mt-5 leading-relaxed text-[#4a4d52]">{lang === "de" ? "TPU (thermoplastisches Polyurethan) ist ein thermoplastischer Elastomer. Es lässt sich wie ein klassischer Kunststoff im Spritzguss und in der Extrusion verarbeiten, behält dabei jedoch elastische, gummiähnliche Eigenschaften. Dadurch ist TPU ideal für flexible, abriebfeste und chemikalienbeständige Bauteile." : "TPU (thermoplastic polyurethane) is a thermoplastic elastomer. It can be processed like a classic plastic in injection molding and extrusion while retaining elastic, rubber-like properties. This makes TPU ideal for flexible, abrasion-resistant and chemical-resistant parts."}</p>
          </Reveal>
          <Reveal delay={0.12}><img src={IMAGES.macro} alt="Makroaufnahme von transparentem TPU Granulat (Neuware)" className="rounded-2xl border border-[#e4e9f0] shadow-xl" loading="lazy" /></Reveal>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="bg-[#f6f8fb] py-24">
        <div className="pn-container">
          <Reveal><p className="overline">{lang === "de" ? "Vorteile" : "Advantages"}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "Eigenschaften von TPU" : "Properties of TPU"}</h2></Reveal>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2">
            {ADV.map((a) => { const c = L(a); return (
              <motion.div key={c[0]} variants={itemVariant} className="pn-card flex gap-4 p-6">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#16181D] text-white"><Check size={17} /></span>
                <div><h3 className="font-head text-lg font-semibold text-[#16181D]">{c[0]}</h3><p className="mt-1 text-sm text-[#4a4d52]">{c[1]}</p></div>
              </motion.div>
            ); })}
          </Stagger>
        </div>
      </section>

      {/* SHORE */}
      <section className="py-24" id="shore">
        <div className="pn-container">
          <Reveal><p className="overline">{t("common.hardness")}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "Shore-Härte: 60 A bis 80 D" : "Shore hardness: 60 A to 80 D"}</h2></Reveal>
          <div className="mt-10 rounded-2xl border border-[#e4e9f0] p-8"><ShoreScale lang={lang} /></div>
        </div>
      </section>

      {/* PROCESSING: injection + extrusion */}
      <section className="bg-[#16181D] py-24 text-white grain">
        <div className="pn-container space-y-16">
          <Reveal><p className="overline">{t("common.method")}</p>
            <h2 className="mt-3 font-head text-3xl font-light tracking-tight md:text-4xl">{lang === "de" ? "TPU Verarbeitung: Extrusion & Spritzguss" : "TPU processing: extrusion & injection molding"}</h2></Reveal>
          <div className="grid gap-8 lg:grid-cols-2" id="extrusion">
            <Reveal><div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <h3 className="font-head text-xl font-semibold">Extrusion</h3>
              <p className="mt-2 text-sm text-white/70">{lang === "de" ? "Schläuche, Profile, Folien und Kabelummantelungen." : "Tubing, profiles, films and cable jacketing."}</p>
              <img src={IMAGES.extrusion} alt="TPU Extrusion – Extruder mit Schmelzefluss zu Schlauch/Profil" className="mt-4 w-full rounded-xl border border-white/10" loading="lazy" />
              <div className="mt-4 rounded-xl bg-white p-4"><ProcessFlow mode="extrusion" /></div>
              <Link to="/tpu/extrusion" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#F0A26A]">{lang === "de" ? "Mehr zu TPU Extrusion" : "More on TPU extrusion"} <ArrowRight size={14} /></Link>
            </div></Reveal>
            <Reveal delay={0.1} ><div id="spritzguss" className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <h3 className="font-head text-xl font-semibold">{lang === "de" ? "Spritzguss" : "Injection molding"}</h3>
              <p className="mt-2 text-sm text-white/70">{lang === "de" ? "Technische Bauteile, Dichtungen und Funktionselemente." : "Technical parts, seals and functional elements."}</p>
              <img src={IMAGES.injection} alt="TPU Spritzguss – Spritzgießmaschine mit technischem Bauteil" className="mt-4 w-full rounded-xl border border-white/10" loading="lazy" />
              <div className="mt-4 rounded-xl bg-white p-4"><ProcessFlow mode="injection" /></div>
              <Link to="/tpu/spritzguss" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#F0A26A]">{lang === "de" ? "Mehr zu TPU Spritzguss" : "More on TPU injection molding"} <ArrowRight size={14} /></Link>
            </div></Reveal>
          </div>
        </div>
      </section>

      {/* PROCESSING NOTES */}
      <section className="py-24">
        <div className="pn-container grid gap-6 md:grid-cols-3">
          {[
            { icon: Droplets, t: { de: "Trocknung", en: "Drying" }, d: { de: "80–110 °C, 2–4 h auf Restfeuchte < 0,02 % vor der Verarbeitung.", en: "80–110 °C, 2–4 h to residual moisture < 0.02 % before processing." } },
            { icon: Thermometer, t: { de: "Schmelzetemperatur", en: "Melt temperature" }, d: { de: "Typisch 190–220 °C je nach Typ und Shore-Härte.", en: "Typically 190–220 °C depending on grade and Shore hardness." } },
            { icon: Layers, t: { de: "Farboptionen", en: "Color options" }, d: { de: "Natur, transparent und über Masterbatch individuell einfärbbar.", en: "Natural, transparent and individually colorable via masterbatch." } },
          ].map((x) => (
            <Reveal key={pick(x.t)}><div className="pn-card h-full p-7">
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><x.icon size={20} /></span>
              <h3 className="mt-4 font-head text-lg font-semibold text-[#16181D]">{pick(x.t)}</h3>
              <p className="mt-2 text-sm text-[#4a4d52]">{pick(x.d)}</p>
            </div></Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-[#f6f8fb] py-24">
        <div className="pn-container">
          <Reveal><p className="overline">{lang === "de" ? "Materialtypen" : "Material grades"}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "TPU Kategorien" : "TPU categories"}</h2></Reveal>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TPU_CATEGORIES.map((cat) => { const c = pick(cat); return (
              <motion.div key={cat.key} variants={itemVariant} className="pn-card p-6">
                <h3 className="font-head text-lg font-semibold text-[#16181D]">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4a4d52]">{c.d}</p>
              </motion.div>
            ); })}
          </Stagger>
        </div>
      </section>

      {/* PRODUCT TABLE */}
      <section className="py-24" id="produkte">
        <div className="pn-container">
          <Reveal><p className="overline">NORDFLEX® TPU</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "TPU Produktübersicht" : "TPU product range"}</h2>
            <p className="mt-3 text-sm text-[#4a4d52]">{lang === "de" ? "Lagerbestände und Preise individuell – Verfügbarkeit auf Anfrage und im Kundenportal." : "Stock and prices individual – availability on request and in the customer portal."}</p>
          </Reveal>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TPU_PRODUCTS.map((p) => (
              <motion.div key={p.name} variants={itemVariant} className="pn-card p-6" data-testid={`tpu-product-${p.shore.replace(/\s/g,"")}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-[#E2611C]">TPU</span>
                  <span className="rounded-full bg-[#f0f2f5] px-2.5 py-1 text-xs font-medium text-[#4a4d52]">{t("common.onRequest")}</span>
                </div>
                <h3 className="mt-3 font-head text-lg font-semibold text-[#16181D]">{p.name}</h3>
                <dl className="mt-4 space-y-1.5 text-sm text-[#4a4d52]">
                  <div className="flex justify-between"><dt>{t("common.hardness")}</dt><dd className="font-medium text-[#16181D]">{p.shore}</dd></div>
                  <div className="flex justify-between"><dt>{t("common.material")}</dt><dd className="font-medium text-[#16181D]">{p.type}</dd></div>
                  <div className="flex justify-between"><dt>{t("common.method")}</dt><dd className="font-medium text-[#16181D]">{p.method}</dd></div>
                </dl>
                <div className="mt-5 flex gap-2">
                  <Link to="/kontakt" className="btn-primary !px-4 !py-2 text-sm">{t("cta.request")}</Link>
                  <Link to="/produkte/nordflex-tpu" className="btn-ghost !px-4 !py-2 text-sm">{t("cta.details")}</Link>
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TPU APPLICATION SUB-PAGES + COMPARISON */}
      <section className="bg-[#f6f8fb] py-24">
        <div className="pn-container">
          <Reveal><p className="overline">{lang === "de" ? "TPU Anwendungen" : "TPU applications"}</p>
            <h2 className="mt-3 font-head text-3xl font-medium tracking-tight text-[#16181D] md:text-4xl">{lang === "de" ? "Anwendungsspezifische TPU-Seiten" : "Application-specific TPU pages"}</h2></Reveal>
          <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TPU_APP_NAV.map(([path, label]) => (
              <motion.div key={path} variants={itemVariant}>
                <Link to={path} className="pn-card flex items-center justify-between p-5" data-testid={`tpu-link-${path.split("/").pop()}`}>
                  <span className="font-head font-medium text-[#16181D]">{label[lang]}</span>
                  <ArrowRight size={16} className="text-[#E2611C]" />
                </Link>
              </motion.div>
            ))}
          </Stagger>
          <div className="mt-14">
            <Reveal><h3 className="mb-6 font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "TPU für Extrusion vs. Spritzguss" : "TPU for extrusion vs. injection molding"}</h3></Reveal>
            <ComparisonTable data={TPU_COMPARE} caption="TPU Extrusion vs Spritzguss" />
          </div>
        </div>
      </section>

      <FaqBlock items={FAQ} />
    </>
  );
}
